import InfoRounded from '@mui/icons-material/InfoRounded';
import Box, { type BoxProps } from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useResourceInfoContext } from '@openx/components-core/lib/Form/context/ResourceInfoContext';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { ToggleButtonGroupDesignSystemScope } from '@openx/components-core/lib/ToggleButtonGroupField/ToggleButtonGroupDesignSystemScope';
import { useIsAppInventoryV2Enabled } from '@openx/components-targeting/Targeting/AppInventory/utils/useIsFeatureEnabled';
import { Attention } from '@openx/components-targeting/Targeting/Attention/Attention';
import { criteriasOptions } from '@openx/components-targeting/Targeting/constants';
import { ChangesFromInfoTag } from '@openx/components-targeting/Targeting/shared/ChangesFromInfoTag';
import { useCtvValidation } from '@openx/components-targeting/Targeting/shared/CtvValidation/utils/useCtvValidation';
import { DimensionChip } from '@openx/components-targeting/Targeting/shared/DimensionChip/DimensionChip';
import { ExcludeBelowAverageDomains } from '@openx/components-targeting/Targeting/shared/ExcludeBelowAverageDomains/ExcludeBelowAverageDomains';
import { ExcludeIndirect } from '@openx/components-targeting/Targeting/shared/ExcludeIndirect/ExcludeIndirect';
import { ExcludeMFA } from '@openx/components-targeting/Targeting/shared/ExcludeMFA/ExcludeMFA';
import { RboDisplayedAlertProvider } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/components/RboDisplayedAlertContext';
import { RboGroupValidationAlert } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/components/RboGroupValidationAlert/RboGroupValidationAlert';
import { RetargetingAquaValidationAlert } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/components/RetargetingAquaValidationAlert/RetargetingAquaValidationAlert';
import { RequiredTargetingSection } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/RequiredTargetingSection';
import {
  TargetingFieldPathProvider,
  TargetingProvider,
  useTargetingContext,
} from '@openx/components-targeting/utils/context/TargetingContext';
import { ALLOWLIST_TARGETING_ATTENTION, ENABLED_TARGETING_ATTENTION } from '@openx/constants/feature-flag-keys';
import type { ConstValues } from '@openx/types/helpers';
import { PackageFields, type PackageFormState } from '@openx/types/package';
import { AppInventoryVersion } from '@openx/types/targeting/appInventory';
import { DomainAppInventoryOperator, UrlTargetingType } from '@openx/types/targeting/domains';
import { InventoryFormatTypeOption } from '@openx/types/targeting/formatType';
import { TargetingFields, type TargetingFieldsValues, type TargetingProps } from '@openx/types/targeting/targeting';
import type { TargetingAppInventory } from '@openx/types/targeting/targetingAppInventoryTypes';
import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets';
import { ComparisonType, Criteria, type CriteriaValue, Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { TechnologyAndDevicesOption } from '@openx/types/targeting/technologyAndDevices';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import {
  type FC,
  Fragment,
  type FunctionComponent,
  type JSX,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTargeting } from '../../../utils/hooks/useTargeting';
import { TARGETING_DESCRIPTION } from '../constants';
import { useTargetingBlockingRules } from '../targetingRules/useTargetingBlockingRules';
import { useInventoryQualityProps } from '../useInventoryQualityProps';
import type { InventoryQualityValue } from '../useIqScoringAutoCheck';

const StyledToggleGroupTitle = (props: BoxProps) => (
  <Box
    {...props}
    sx={[
      {
        color: 'text.secondary',
        fontSize: '13px',
        fontWeight: 'fontWeightBold',
        lineHeight: '32px',
        marginLeft: 1,
        textTransform: 'uppercase',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

const StyledTargetingSubSectionTitle = (props: TypographyProps) => (
  <Typography
    variant="h3"
    {...props}
    sx={[
      {
        width: '100%',
        mb: 1.5,
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

type TargetingSectionProps = {
  initialValues?: PackageFormState;
};

type RHFTargetingComponentProps = Omit<TargetingProps<any, any>, 'targetingParams'> & {
  Component: FunctionComponent<TargetingProps<any, any> & { ref?: RefObject<any> }>;
};

type BlockingReasonMap = Partial<Record<string, string>>;

const RHFTargetingComponent: FC<RHFTargetingComponentProps> = ({ Component, ...props }) => {
  const nextName = `${PackageFields.TARGETING}.${props.field}`;
  const observableTargetingParams = useWatch({
    name: nextName,
  });

  const observableAccountUid = useWatch({
    name: PackageFields.ACCOUNT_UID,
  });

  const { register } = useFormContext();

  return (
    <div {...register(nextName, { shouldUnregister: true })} style={{ width: '100%' }}>
      <TargetingFieldPathProvider value={props.field}>
        <Component
          key={`${props.field}-${observableAccountUid}`}
          {...props}
          targetingParams={observableTargetingParams}
        />
      </TargetingFieldPathProvider>
    </div>
  );
};

const isAppInventoryExcludeMode = (appInventory: TargetingAppInventory | null | undefined): boolean => {
  if (!appInventory) return false;

  const hasIncludedAppGroup = appInventory.appgroup?.op === Intersect.INTERSECTS && Boolean(appInventory.appgroup?.val);

  const hasIncludedBundleItems =
    Array.isArray(appInventory.app_bundle_id?.val) &&
    appInventory.app_bundle_id.val.some(
      item => item?.op === ComparisonType.EQUALS || item?.op === ComparisonType.BEGINS_WITH
    );

  const hasIncludedInventoryList = Boolean(appInventory.app_bundle_id?.inventorylist?.id);

  const hasInclude = hasIncludedAppGroup || hasIncludedBundleItems || hasIncludedInventoryList;

  const hasExcludedAppGroup = appInventory.appgroup?.op === Intersect.NOT_INTERSECTS;

  const hasExcludedBundleItems =
    Array.isArray(appInventory.app_bundle_id?.val) &&
    appInventory.app_bundle_id.val.some(
      item => item?.op === ComparisonType.DOES_NOT_EQUAL || item?.op === ComparisonType.DOES_NOT_BEGIN_WITH
    );

  const hasExclude = hasExcludedAppGroup || hasExcludedBundleItems;

  return hasExclude && !hasInclude;
};

const getValueByPath = (value: unknown, path: string): unknown => {
  return path.split('.').reduce<unknown>((accumulator, key) => {
    if (!accumulator || typeof accumulator !== 'object') {
      return undefined;
    }

    return (accumulator as Record<string, unknown>)[key];
  }, value);
};

const hasConfiguredTargetingValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value !== 'object') {
    return Boolean(value);
  }

  const record = value as Record<string, unknown>;

  if (Object.hasOwn(record, 'val')) {
    return (
      hasConfiguredTargetingValue(record.val) ||
      hasConfiguredTargetingValue(record.inventorylist_id) ||
      hasConfiguredTargetingValue(record.inventorylist)
    );
  }

  if (Object.hasOwn(record, 'includes') || Object.hasOwn(record, 'excludes')) {
    return hasConfiguredTargetingValue(record.includes) || hasConfiguredTargetingValue(record.excludes);
  }

  return Object.values(record).some(hasConfiguredTargetingValue);
};

const getDisabledReasonForFieldPath = (
  fieldPath: string,
  blockedTargetingFields: BlockingReasonMap
): string | undefined => {
  const exactReason = blockedTargetingFields[fieldPath];

  if (exactReason) {
    return exactReason;
  }

  const pathParts = fieldPath.split('.');

  for (let index = pathParts.length - 1; index > 0; index -= 1) {
    const parentPath = pathParts.slice(0, index).join('.');
    const parentReason = blockedTargetingFields[parentPath];

    if (parentReason) {
      return parentReason;
    }
  }

  const disabledChildFieldsByParent: Record<string, string[]> = {
    [TargetingFields.APP_INVENTORY]: ['app_bundle_id', 'appgroup'],
  };

  const childFields = disabledChildFieldsByParent[fieldPath];

  if (childFields?.length) {
    const childReasons = childFields.map(childField => blockedTargetingFields[`${fieldPath}.${childField}`]);

    if (childReasons.every(Boolean)) {
      return childReasons[0];
    }
  }

  return undefined;
};

const getErrorReasonForFieldPath = (
  fieldPath: string,
  fieldsWithErrors: BlockingReasonMap,
  targeting: unknown
): string | undefined => {
  const exactReason = fieldsWithErrors[fieldPath];

  if (exactReason) {
    return exactReason;
  }

  const descendantPrefix = `${fieldPath}.`;

  return Object.entries(fieldsWithErrors).find(([blockedFieldPath]) => {
    if (!blockedFieldPath.startsWith(descendantPrefix)) {
      return false;
    }

    return hasConfiguredTargetingValue(getValueByPath(targeting, blockedFieldPath));
  })?.[1];
};

const hasAnyAppInventoryValues = (appInventory: TargetingAppInventory | null | undefined): boolean => {
  if (!appInventory) return false;

  const hasAppGroupValue = Boolean(appInventory.appgroup?.val);
  const hasBundleValues = Array.isArray(appInventory.app_bundle_id?.val) && appInventory.app_bundle_id!.val.length > 0;
  const hasInventoryList = Boolean(appInventory.app_bundle_id?.inventorylist?.id);

  return hasAppGroupValue || hasBundleValues || hasInventoryList;
};

type AppInventoryVersionValue = ConstValues<typeof AppInventoryVersion>;

export const TargetingSection = (_: TargetingSectionProps) => {
  const { isGrowthbookFeatureOn, isOa, isPub } = usePermissionContext();

  const isAttentionOn = useMemo(() => isGrowthbookFeatureOn(ENABLED_TARGETING_ATTENTION), [isGrowthbookFeatureOn]);
  const isAttentionAllowed = useMemo(
    () => isGrowthbookFeatureOn(ALLOWLIST_TARGETING_ATTENTION),
    [isGrowthbookFeatureOn]
  );

  const { t } = useTranslation();
  const targetingFields = useTargeting();
  const criterias = criteriasOptions(t);
  const { setValue, trigger } = useFormContext();

  const targetingContext = useTargetingContext();
  const { useCurrentInstanceState, useIsRetargetingAllowed } = targetingContext;
  const { isBefInstance } = useCurrentInstanceState();

  // Deal: org flag + Aqua DSP. Package create/edit: org flag only.
  const isRetargetingAllowed = useIsRetargetingAllowed?.() ?? false;

  const { name: resourceName } = useResourceInfoContext();

  const shouldEnforceAndOperator = resourceName === 'deal' || resourceName === 'package';

  const observableAccountUid = useWatch({
    name: PackageFields.ACCOUNT_UID,
  });
  const observableTargeting = useWatch({
    name: PackageFields.TARGETING,
  });

  const hasRetargetingValue = !!observableTargeting?.[TargetingFields.RETARGETING]?.val;
  const targeting = useMemo(
    () =>
      targetingFields.filter(
        item => item.field !== TargetingFields.RETARGETING || isRetargetingAllowed || hasRetargetingValue
      ),
    [targetingFields, isRetargetingAllowed, hasRetargetingValue]
  );

  const { blockedTargetingFields, fieldsWithErrors } = useTargetingBlockingRules({
    targeting: observableTargeting,
  });

  const getDisabledReasonFromBlockingRules = useCallback(
    (fieldPath: string) => getDisabledReasonForFieldPath(fieldPath, blockedTargetingFields),
    [blockedTargetingFields]
  );

  const getErrorReasonFromBlockingRules = useCallback(
    (fieldPath: string) => getErrorReasonForFieldPath(fieldPath, fieldsWithErrors, observableTargeting),
    [fieldsWithErrors, observableTargeting]
  );

  const targetingBlockingRules = useMemo(
    () => ({
      blockedTargetingFields,
      getDisabledReason: getDisabledReasonFromBlockingRules,
      isFieldDisabled: (fieldPath: string) => Boolean(getDisabledReasonFromBlockingRules(fieldPath)),
      getErrorReason: getErrorReasonFromBlockingRules,
      hasFieldError: (fieldPath: string) => Boolean(getErrorReasonFromBlockingRules(fieldPath)),
    }),
    [blockedTargetingFields, getDisabledReasonFromBlockingRules, getErrorReasonFromBlockingRules]
  );

  const targetingContextWithBlockingRules = useMemo(
    () => ({
      ...targetingContext,
      targetingBlockingRules,
    }),
    [targetingContext, targetingBlockingRules]
  );

  const observableInventoryFormat = useWatch({
    name: `${PackageFields.TARGETING}.${[TargetingFields.INVENTORY_FORMAT]}`,
  });

  const isCTV = useMemo(
    () => observableInventoryFormat?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val === 'CTV',
    [observableInventoryFormat]
  );

  const formatType: RequiredTargetingFormType | null = observableTargeting?.[TargetingFields.INVENTORY_FORMAT] ?? null;
  const adPlacement: string | null | undefined = formatType?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val;
  const distributionChannel: string | null | undefined =
    formatType?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val;

  const inventoryQuality: InventoryQualityValue = observableTargeting?.[TargetingFields.INVENTORY_QUALITY];

  const targetingDimension = observableTargeting?.[TargetingFields.DIMENSION];
  const excludeIndirect = observableTargeting[TargetingFields.EXCLUDE_INDIRECT];
  const includeOpenBiddingInventory = observableTargeting[TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY];
  const excludeMFA = observableTargeting?.[TargetingFields.METACATEGORY]?.[TargetingFields.EXCLUDE_MFA] ?? false;

  useEffect(() => {
    if (!shouldEnforceAndOperator) return;

    if (targetingDimension !== Criteria.ALL) {
      setValue(`${PackageFields.TARGETING}.${TargetingFields.DIMENSION}`, Criteria.ALL, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [shouldEnforceAndOperator, targetingDimension, setValue]);

  const domainsTargeting = observableTargeting?.[TargetingFields.URL_TARGETING];
  const appInventoryTargeting = observableTargeting?.[TargetingFields.APP_INVENTORY] as TargetingAppInventory | null;

  const hasDomainsValues = useMemo(() => {
    return !!domainsTargeting && (!!domainsTargeting.inventorylist_id || !!domainsTargeting.urls?.length);
  }, [domainsTargeting]);

  const hasAppInventoryValues = useMemo(() => hasAnyAppInventoryValues(appInventoryTargeting), [appInventoryTargeting]);

  const isDomainsExcludeMode = useMemo(() => {
    return domainsTargeting?.type === UrlTargetingType.blacklist;
  }, [domainsTargeting]);

  const isAppInventoryExclude = useMemo(
    () => isAppInventoryExcludeMode(appInventoryTargeting),
    [appInventoryTargeting]
  );

  const computedDomainAppOperator = useMemo(() => {
    if (!hasDomainsValues || !hasAppInventoryValues) return undefined;

    return !isDomainsExcludeMode && !isAppInventoryExclude
      ? DomainAppInventoryOperator.OR
      : DomainAppInventoryOperator.AND;
  }, [hasDomainsValues, hasAppInventoryValues, isDomainsExcludeMode, isAppInventoryExclude]);

  useEffect(() => {
    if (!shouldEnforceAndOperator) return;

    if (observableTargeting?.[TargetingFields.APP_INVENTORY] == null) return;
    if (!computedDomainAppOperator) return;

    const currentOperator = domainsTargeting?.domain_app_inventory_operator;

    if (currentOperator !== computedDomainAppOperator) {
      setValue(
        `${PackageFields.TARGETING}.${TargetingFields.URL_TARGETING}.domain_app_inventory_operator`,
        computedDomainAppOperator,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    }
  }, [
    shouldEnforceAndOperator,
    observableTargeting,
    computedDomainAppOperator,
    domainsTargeting?.domain_app_inventory_operator,
    setValue,
  ]);

  const onTargetingUpdate = useCallback(
    (field: TargetingFieldsValues, newData): void => {
      setValue(`${PackageFields.TARGETING}.${field}`, newData, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const [requiredTargetingManuallyUpdated, setRequiredTargetingManuallyUpdated] = useState(false);

  const onRequiredTargetingUpdate = useCallback(
    (field: TargetingFieldsValues, newData, shouldValidate?: boolean): void => {
      if (shouldValidate) setRequiredTargetingManuallyUpdated(true);

      setValue(`${PackageFields.TARGETING}.${field}`, newData, {
        shouldDirty: true,
        shouldValidate: !!shouldValidate,
      });
    },
    [setValue]
  );

  const onTargetingExcludeMFAUpdate = useCallback(
    (newData: boolean): void => {
      setValue(`${PackageFields.TARGETING}.${TargetingFields.METACATEGORY}.${TargetingFields.EXCLUDE_MFA}`, newData, {
        shouldDirty: true,
        shouldValidate: true,
      });
      trigger();
    },
    [setValue, trigger]
  );

  const onDimensionChange = useCallback(
    (_, dimension) => {
      if (shouldEnforceAndOperator) return;

      dimension &&
        setValue(`${PackageFields.TARGETING}.${TargetingFields.DIMENSION}`, dimension, {
          shouldDirty: true,
          shouldValidate: true,
        });
      trigger();
    },
    [setValue, trigger, shouldEnforceAndOperator]
  );

  const isNewFormat =
    !observableTargeting[TargetingFields.FORMAT_TYPE]?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val &&
    !observableTargeting[TargetingFields.FORMAT_TYPE]?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val &&
    !observableTargeting[TargetingFields.TECHNOLOGY_AND_DEVICES]?.[TechnologyAndDevicesOption.DEVICE_TYPE]?.val;

  const isInventoryV2 = useIsAppInventoryV2Enabled(observableTargeting);

  const iqCheckboxProps = useInventoryQualityProps({
    isPub,
    adPlacement,
    distributionChannel,
    inventoryQuality,
  });

  const showIsAttention = isOa && isAttentionOn && isAttentionAllowed;

  const domainAppInventoryInfoText = useMemo(
    () =>
      t(
        'Domains and App Inventory represent separate environments (web vs. app). Using OR ensures the deal can serve on either web domains or apps, instead of unintentionally requiring both and severely limiting eligible inventory.'
      ),
    [t]
  );

  const shouldShowDomainAppInventoryInfo =
    shouldEnforceAndOperator && computedDomainAppOperator === DomainAppInventoryOperator.OR;

  const renderContent = () => (
    <>
      <Grid
        container
        sx={{
          alignItems: 'center',
        }}
      >
        {!isNewFormat && (
          <RboDisplayedAlertProvider>
            <Stack
              direction="column"
              sx={{
                alignItems: 'stretch',
                width: '100%',
                gap: 1,
              }}
            >
              <RboGroupValidationAlert />
              <RetargetingAquaValidationAlert />
            </Stack>
          </RboDisplayedAlertProvider>
        )}
        {isNewFormat && (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              width: '100%',
              gap: 1,
            }}
          >
            <StyledTargetingSubSectionTitle data-test="core-targeting-settings-title" sx={{ mt: 1.5 }}>
              {t('Core Targeting')}
            </StyledTargetingSubSectionTitle>

            {!requiredTargetingManuallyUpdated && <ChangesFromInfoTag fieldName={TargetingFields.INVENTORY_FORMAT} />}
          </Stack>
        )}

        {isNewFormat && (
          <RHFTargetingComponent
            field={TargetingFields.INVENTORY_FORMAT}
            name={TargetingFields.INVENTORY_FORMAT}
            Component={RequiredTargetingSection as RHFTargetingComponentProps['Component']}
            onFieldUpdate={onRequiredTargetingUpdate}
          />
        )}

        <StyledTargetingSubSectionTitle data-test="targeting-settings-quality-filters-title">
          {t('Inventory Quality')}
        </StyledTargetingSubSectionTitle>

        <ExcludeMFA excludeMFAValue={excludeMFA} onFieldUpdate={onTargetingExcludeMFAUpdate} />

        {iqCheckboxProps && (
          <ExcludeBelowAverageDomains
            readonly={false}
            disabled={iqCheckboxProps.disabled}
            error={iqCheckboxProps.error}
            helperText={iqCheckboxProps.helperText}
          />
        )}

        {isBefInstance && (
          <ExcludeIndirect
            excludeIndirectValue={excludeIndirect}
            includeOpenBiddingInventory={includeOpenBiddingInventory}
            onFieldUpdate={onTargetingUpdate}
          />
        )}

        {showIsAttention && <Attention disabled={!isCTV} />}

        {targeting.length > 0 && (
          <StyledTargetingSubSectionTitle data-test="advanced-targeting-settings-title" sx={{ mt: 1.5 }}>
            {t('Advanced Targeting')}
          </StyledTargetingSubSectionTitle>
        )}

        {!shouldEnforceAndOperator && (
          <>
            <ToggleButtonGroupDesignSystemScope>
              <ToggleButtonGroup exclusive value={targetingDimension} onChange={onDimensionChange}>
                {criterias.map(({ label, value }) => (
                  <ToggleButton
                    key={value}
                    id={String(value)}
                    value={value}
                    data-test={`match-${String(value).toLowerCase()}`}
                  >
                    <Typography component="span">{label}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </ToggleButtonGroupDesignSystemScope>
            <StyledToggleGroupTitle>{t('of the following:')}</StyledToggleGroupTitle>
          </>
        )}
      </Grid>

      <RenderTargetingFields
        targeting={targeting}
        onTargetingUpdate={onTargetingUpdate}
        observableAccountUid={observableAccountUid ?? undefined}
        targetingDimension={targetingDimension}
        isNewFormat={isNewFormat}
        inventoryVersion={
          isInventoryV2 ? AppInventoryVersion.APP_INVENTORY_EXCLUDED_V1 : AppInventoryVersion.APP_INVENTORY_INCLUDED_V1
        }
        domainAppInventoryOperator={computedDomainAppOperator}
        shouldShowDomainAppInventoryInfo={shouldShowDomainAppInventoryInfo}
        domainAppInventoryInfoText={domainAppInventoryInfoText}
      />
    </>
  );

  return (
    <FormSection name="Targeting" title={t('Targeting Criteria')} description={TARGETING_DESCRIPTION(t)} divider>
      <TargetingProvider targetingConfig={targetingContextWithBlockingRules}>{renderContent()}</TargetingProvider>
    </FormSection>
  );
};

type RenderTargetingFieldsProps = {
  targeting: ReturnType<typeof useTargeting>;
  onTargetingUpdate: (field: TargetingFieldsValues, newData: unknown) => void;
  observableAccountUid: string | undefined;
  targetingDimension: CriteriaValue | null | undefined;
  isNewFormat: boolean;
  inventoryVersion: AppInventoryVersionValue;
  domainAppInventoryOperator: DomainAppInventoryOperator | undefined;
  shouldShowDomainAppInventoryInfo: boolean;
  domainAppInventoryInfoText: string;
};

const RenderTargetingFields = ({
  targeting,
  onTargetingUpdate,
  observableAccountUid,
  targetingDimension,
  isNewFormat,
  inventoryVersion,
  domainAppInventoryOperator,
  shouldShowDomainAppInventoryInfo,
  domainAppInventoryInfoText,
}: RenderTargetingFieldsProps) => {
  const ctvValidationMethods = useCtvValidation();
  const { t } = useTranslation();

  const orPairWrapperSx = {
    position: 'relative',
    pl: 2.5,
    ml: 0,
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: '2px',
      borderRadius: '2px',
      bgcolor: 'divider',
    },
  } as const;

  const elements: JSX.Element[] = [];

  for (let i = 0; i < targeting.length; i++) {
    const item = targeting[i];
    const { field, name, TargetingComponent } = item;

    if (field === TargetingFields.FORMAT_TYPE && isNewFormat) continue;
    if (field === TargetingFields.APP_INVENTORY && inventoryVersion === AppInventoryVersion.APP_INVENTORY_INCLUDED_V1)
      continue;

    const nextItem = targeting[i + 1];
    const nextField = nextItem?.field;

    const isBetweenDomainsAndAppInventory =
      field === TargetingFields.URL_TARGETING && nextField === TargetingFields.APP_INVENTORY;

    const isOrPair = isBetweenDomainsAndAppInventory && domainAppInventoryOperator === DomainAppInventoryOperator.OR;

    if (isOrPair && nextItem) {
      const isOrChip = String(domainAppInventoryOperator) === Criteria.ANY;

      elements.push(
        <Box key={`${field}-${nextField}-or-pair`} sx={orPairWrapperSx} data-test="or-pair-wrapper">
          <RHFTargetingComponent
            Component={TargetingComponent}
            name={name}
            field={field}
            onFieldUpdate={onTargetingUpdate}
            accountUid={observableAccountUid}
            customValidationMethods={ctvValidationMethods}
            isNewFormat={isNewFormat}
            inventoryVersion={inventoryVersion}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DimensionChip targetingDimension={domainAppInventoryOperator} isSubsection={isOrChip} />

            {shouldShowDomainAppInventoryInfo && (
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: 560, p: 2 },
                  },
                }}
                title={
                  <Box>
                    <Typography variant="body2">{domainAppInventoryInfoText}</Typography>
                  </Box>
                }
              >
                <IconButton size="small" aria-label={t('Domains and App Inventory info')} sx={{ ml: 0.5 }}>
                  <InfoRounded fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <RHFTargetingComponent
            Component={nextItem.TargetingComponent}
            name={nextItem.name}
            field={nextItem.field}
            onFieldUpdate={onTargetingUpdate}
            accountUid={observableAccountUid}
            customValidationMethods={ctvValidationMethods}
            isNewFormat={isNewFormat}
            inventoryVersion={inventoryVersion}
          />
        </Box>
      );

      i += 1;
      continue;
    }

    const shouldShowChip = i < targeting.length - 1;

    const chipDimension =
      (isBetweenDomainsAndAppInventory && domainAppInventoryOperator
        ? domainAppInventoryOperator
        : targetingDimension) ?? Criteria.ALL;

    const isOrChip = String(chipDimension) === Criteria.ANY;

    elements.push(
      <Fragment key={field}>
        <RHFTargetingComponent
          Component={TargetingComponent}
          name={name}
          field={field}
          onFieldUpdate={onTargetingUpdate}
          accountUid={observableAccountUid}
          customValidationMethods={ctvValidationMethods}
          isNewFormat={isNewFormat}
          inventoryVersion={inventoryVersion}
        />

        {shouldShowChip && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DimensionChip targetingDimension={chipDimension} isSubsection={isOrChip} />

            {isBetweenDomainsAndAppInventory && shouldShowDomainAppInventoryInfo && (
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: 560, p: 2 },
                  },
                }}
                title={
                  <Box>
                    <Typography variant="body2">{domainAppInventoryInfoText}</Typography>
                  </Box>
                }
              >
                <IconButton size="small" aria-label={t('Domains and App Inventory info')} sx={{ ml: 0.5 }}>
                  <InfoRounded fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Fragment>
    );
  }

  return <>{elements}</>;
};
